import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { THEME, gradients, shadows } from '../constants/theme';
import { 
  EnvelopeIcon, 
  PhoneIcon, 
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const Support = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    type: 'technical',
    subject: '',
    description: '',
    email: user?.email || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const { data, error } = await supabase
        .from('tickets')
        .insert([{
          user_id: user?.id || null,
          type: formData.type,
          subject: formData.subject,
          description: formData.description,
          email: formData.email,
          status: 'new'
        }]);

      if (error) throw error;

      setSubmitStatus({
        type: 'success',
        message: 'Your support ticket has been submitted successfully. We\'ll get back to you soon!'
      });
      setFormData({
        type: 'technical',
        subject: '',
        description: '',
        email: user?.email || ''
      });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Failed to submit support ticket. Please try again or contact us via email.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: THEME.background }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="rounded-2xl shadow-xl mb-12 p-8 text-white" style={{ background: gradients.primary }}>
          <h1 className="text-4xl font-bold mb-4">How Can We Help?</h1>
          <p className="text-lg opacity-90 mb-8">
            We're here to help you get the most out of Aided. Choose your preferred way to get support below.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
              <EnvelopeIcon className="h-8 w-8 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Email Support</h3>
              <p className="opacity-90">
                Send us an email at{' '}
                <a href="mailto:support@aided.com.au" className="underline hover:no-underline">
                  support@aided.com.au
                </a>
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
              <ClockIcon className="h-8 w-8 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Response Time</h3>
              <p className="opacity-90">We aim to respond within 24 hours during business days</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
              <PhoneIcon className="h-8 w-8 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Business Hours</h3>
              <p className="opacity-90">Monday to Friday, 9 AM - 5 PM AEST</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Support Information */}
          <div className="rounded-2xl shadow-xl p-8" style={{ backgroundColor: THEME.cardbg }}>
            <h2 className="text-2xl font-bold mb-6" style={{ color: THEME.text }}>Support Information</h2>
            <div className="space-y-8" style={{ color: THEME.text }}>
              <div>
                <h3 className="text-xl font-semibold mb-3">What to Expect</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Initial response within 24 business hours</li>
                  <li>Regular updates on your ticket status</li>
                  <li>Follow-up satisfaction survey</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">Support Levels</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Standard Support:</strong> Available to all users</li>
                  <li><strong>Priority Support:</strong> Available to premium subscribers</li>
                  <li><strong>Emergency Support:</strong> Available 24/7 for critical issues</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">Issue Types</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Technical Issues:</strong> Platform functionality, bugs, errors</li>
                  <li><strong>Account Issues:</strong> Login, settings, permissions</li>
                  <li><strong>Billing Issues:</strong> Payments, subscriptions, invoices</li>
                  <li><strong>Feature Requests:</strong> Suggestions for improvements</li>
                  <li><strong>Delete Account:</strong> Request to delete account and all data</li>
                  <li><strong>Other:</strong> Any other issues not covered by the above</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">Tips for Faster Resolution</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide detailed description of the issue</li>
                  <li>Include steps to reproduce the problem</li>
                  <li>Specify your browser/device information</li>
                  <li>Add screenshots if applicable</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Support Form */}
          <div className="rounded-2xl shadow-xl p-8" style={{ backgroundColor: THEME.cardbg }}>
            <h2 className="text-2xl font-bold mb-6" style={{ color: THEME.text }}>Submit a Support Ticket</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label 
                  htmlFor="type" 
                  className="block text-sm font-medium mb-2"
                  style={{ color: THEME.text }}
                >
                  Issue Type
                </label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-opacity-50"
                  style={{ 
                    backgroundColor: THEME.background,
                    color: THEME.text,
                    borderColor: THEME.border
                  }}
                >
                  <option value="technical">Technical Issue</option>
                  <option value="account">Account Issue</option>
                  <option value="billing">Billing Issue</option>
                  <option value="feature">Feature Request</option>
                  <option value="delete">Request to delete account and all data</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label 
                  htmlFor="subject" 
                  className="block text-sm font-medium mb-2"
                  style={{ color: THEME.text }}
                >
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-opacity-50"
                  style={{ 
                    backgroundColor: THEME.background,
                    color: THEME.text,
                    borderColor: THEME.border
                  }}
                  placeholder="Brief summary of your issue"
                />
              </div>

              <div>
                <label 
                  htmlFor="description" 
                  className="block text-sm font-medium mb-2"
                  style={{ color: THEME.text }}
                >
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-opacity-50"
                  style={{ 
                    backgroundColor: THEME.background,
                    color: THEME.text,
                    borderColor: THEME.border
                  }}
                  placeholder="Please provide detailed information about your issue"
                />
              </div>

              <div>
                <label 
                  htmlFor="email" 
                  className="block text-sm font-medium mb-2"
                  style={{ color: THEME.text }}
                >
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-opacity-50"
                  style={{ 
                    backgroundColor: THEME.background,
                    color: THEME.text,
                    borderColor: THEME.border
                  }}
                  placeholder="Where we can reach you"
                />
              </div>

              {submitStatus && (
                <div className={`p-4 rounded-lg ${
                  submitStatus.type === 'success' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {submitStatus.type === 'success' ? (
                    <div className="flex items-center text-green-800">
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      {submitStatus.message}
                    </div>
                  ) : (
                    <div className="flex items-center text-red-800">
                      <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                      {submitStatus.message}
                    </div>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 px-6 rounded-lg text-white font-medium transition-all duration-200"
                style={{ 
                  background: gradients.primary,
                  opacity: isSubmitting ? 0.7 : 1
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Ticket'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;